// 저장소 안전 래퍼 (frontend-stability Task A-2)
// 배경(Sentry): /client 공유 링크 — 저장소 차단 브라우저(Safari 시크릿/기업 정책)에서
// naked localStorage 접근이 SecurityError throw → React 렌더 크래시.
// 동작: try-catch + 메모리 폴백(세션 내 일관성 유지). 읽기/쓰기/삭제 전부 무throw 보장.
// client 모드 쓰기 차단: brandStorage 우회(safeStorage.setItem 직접호출)까지 막는 backstop은
//   아래 setItem에서 mediaplex_ 키 기준으로 처리(S2 Task 2-A). brandStorage 레벨 가드는 그대로 유지.
import { isClientMode } from "./clientMode.js";
const mem = new Map();

export const safeStorage = {
  getItem(key) {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) return v;
    } catch {
      /* 차단 환경 — 메모리 폴백 */
    }
    return mem.has(key) ? mem.get(key) : null;
  },
  setItem(key, value) {
    // S2 Task 2-A: 공유 뷰어(client)에서 brand/app 영속 쓰기 거부 — 콘솔·딥링크 우회 backstop.
    //   mediaplex_* = getBrandKey 등 모든 brand-scoped/config 키. 비-mediaplex(테마 mpx-theme 등)는 미영향.
    //   B6: isClientMode() 직접 호출 유지(B3a) — permissions.js canWriteLocalStorage는 선언적
    //   미러일 뿐, derivePermissions 배선 시 호출부 입력 오류가 backstop을 뚫으므로 배선 금지.
    if (
      isClientMode() &&
      typeof key === "string" &&
      key.startsWith("mediaplex_")
    ) {
      return;
    }
    mem.set(key, String(value));
    try {
      localStorage.setItem(key, value);
    } catch {
      /* 차단 환경 — 메모리에만 (세션 내 일관) */
    }
  },
  // Use when a UI may report a durable save. The memory fallback alone cannot
  // acknowledge persistence, and failed writes must not alter the visible cache.
  setItemPersisted(key, value) {
    if (isClientMode() && typeof key === "string" && key.startsWith("mediaplex_")) return false;
    try {
      const serialized = String(value);
      if (localStorage.getItem(key) !== serialized) localStorage.setItem(key, serialized);
      if (localStorage.getItem(key) !== serialized) return false;
      mem.set(key, serialized);
      return true;
    } catch { return false; }
  },
  // Related settings must be acknowledged together. Keep React/cache unchanged
  // until all writes verify; restore the previous durable values on failure.
  setItemsPersisted(entries) {
    if (entries.some(([key]) => isClientMode() && key.startsWith("mediaplex_")))
      return { ok: false, restored: true };
    const previous = new Map(), attempted = [];
    try {
      for (const [key] of entries) previous.set(key, localStorage.getItem(key));
      for (const [key, value] of entries) {
        attempted.push(key);
        localStorage.setItem(key, String(value));
        if (localStorage.getItem(key) !== String(value)) throw new Error("Storage verification failed");
      }
      for (const [key, value] of entries) mem.set(key, String(value));
      return { ok: true, restored: true };
    } catch {
      let restored = true;
      for (const key of attempted.reverse()) {
        try {
          const old = previous.get(key);
          if (localStorage.getItem(key) !== old) {
            if (old === null) localStorage.removeItem(key);
            else localStorage.setItem(key, old);
          }
          if (localStorage.getItem(key) !== old) restored = false;
        } catch { restored = false; }
      }
      return { ok: false, restored };
    }
  },
  removeItem(key) {
    mem.delete(key);
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  },
};

// C6 P1 — 레거시 provider 키 부팅 purge.
// 과거 버전이 브라우저에 저장한 Anthropic/Gemini 장수 키를 앱 부팅 시 제거한다.
// provider 자격의 유일 권위는 서버 env 이므로, 이 두 이름은 이제 "지울 대상"으로만
// 존재한다 — 프로덕션 프론트에서 이 이름이 등장해도 되는 곳은 이 목록 하나뿐이다.
// removeItem 이 저장소 차단 환경에서도 무throw 라, 부팅 경로에서 안전하다.
const LEGACY_PROVIDER_KEYS = [
  "mediaplex_ai_api_key",
  "mediaplex_gemini_api_key",
];

export function purgeLegacyProviderKeys() {
  for (const key of LEGACY_PROVIDER_KEYS) safeStorage.removeItem(key);
}

export default safeStorage;
